import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, DollarSign, Calendar, Shield, Eye, Heart, Mail, Link, Award, Clock } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const AffiliateDisclosurePage = () => {
  const [openSections, setOpenSections] = useState({
    whyThisMatters: true,
    howRecommendationsAreCreated: false,
    howAffiliateLinksWork: false,
    editorialIndependence: false,
    ourPartners: false,
    qualityStandards: false,
    transparencyAndTrust: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />

      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Affiliate Disclosure
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                Transparency about how we earn money — and how we stay independent
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: December 8, 2025</span>
          </div>

          {/* Why This Matters */}
          <AccordionItem
            title="Why This Matters"
            isOpen={openSections.whyThisMatters}
            onToggle={() => toggleSection('whyThisMatters')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                At Radiance, transparency is foundational.
                We want you to understand exactly how we earn revenue, how affiliate links work, and how we ensure our recommendations remain completely independent.
              </p>
              <p className="text-sm text-gray-600">
                Radiance may earn a small commission when you purchase a product through one of our affiliate links.
                This never affects the price you pay, and it never determines which products we recommend.
              </p>
              <p className="text-sm text-gray-600">
                Affiliate income simply helps us keep Radiance free to use while maintaining the quality and clarity of our content.
              </p>
            </div>
          </AccordionItem>

          {/* How Our Recommendations Are Created */}
          <AccordionItem
            title="How Our Recommendations Are Created"
            isOpen={openSections.howRecommendationsAreCreated}
            onToggle={() => toggleSection('howRecommendationsAreCreated')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance is built to simplify beauty discovery.
              </p>
              <p className="text-sm text-gray-600">
                We use AI to help surface patterns across the beauty world — what people consistently talk about, repurchase, and love — and then our editorial team shapes that into clear, practical guidance.
              </p>
              <p className="text-sm text-gray-600">
                We don't test every product ourselves, and we don't claim to be chemists or labs.
                Instead, we focus on understanding real user experiences and presenting that information in a helpful, accessible way.
              </p>
              <p className="text-sm text-gray-600">
                As Radiance grows, our long-term vision is to build a community-driven review system powered by your real experiences, creating a platform where beauty users guide each other with authentic insights.
              </p>
            </div>
          </AccordionItem>

          {/* How Affiliate Links Work */}
          <AccordionItem
            title="How Affiliate Links Work"
            isOpen={openSections.howAffiliateLinksWork}
            onToggle={() => toggleSection('howAffiliateLinksWork')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                When you click an affiliate link on Radiance:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• You're directed to the retailer's website</li>
                <li>• If you make a purchase, we may earn a commission</li>
                <li>• Your price stays exactly the same</li>
                <li>• The commission does not influence what we feature or recommend</li>
              </ul>
              <p className="text-sm text-gray-600 mt-4">
                Affiliate partnerships support Radiance financially — never editorially.
              </p>
            </div>
          </AccordionItem>

          {/* Editorial Independence */}
          <AccordionItem
            title="Editorial Independence"
            isOpen={openSections.editorialIndependence}
            onToggle={() => toggleSection('editorialIndependence')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Our editorial decisions are fully separate from our affiliate relationships.
                Every product is selected based on merit, relevance, and what we believe will genuinely help our readers.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">What We Do</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Surface consistent patterns from real beauty conversations</li>
                    <li>• Identify what people genuinely love, use, and repurchase</li>
                    <li>• Provide clear explanations of who a product suits and why</li>
                    <li>• Continually refine our guidance as trends and user insights evolve</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">What We Don't Do</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Accept payment in exchange for positive coverage</li>
                    <li>• Allow brands to influence our rankings or recommendations</li>
                    <li>• Prioritize higher commissions over better products</li>
                    <li>• Hide the fact that a link may generate a commission</li>
                  </ul>
                </div>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Our Promise</p>
                    <p className="text-sm">
                      Editorial independence isn't a feature — it's a promise.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Our Partners */}
          <AccordionItem
            title="Our Partners"
            isOpen={openSections.ourPartners}
            onToggle={() => toggleSection('ourPartners')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance works with a variety of affiliate networks, major retailers, and brand programs so we can offer shopping links that are reliable and convenient.
              </p>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Examples include:</h4>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Well-known beauty retailers</li>
                  <li>• Leading e-commerce platforms</li>
                  <li>• Direct-to-consumer beauty and wellness brands</li>
                </ul>
              </div>
              
              <p className="text-sm text-gray-600">
                These partnerships help support Radiance, but they never shape our editorial voice or product choices.
              </p>
            </div>
          </AccordionItem>

          {/* Quality Standards */}
          <AccordionItem
            title="Quality Standards"
            isOpen={openSections.qualityStandards}
            onToggle={() => toggleSection('qualityStandards')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We only participate in affiliate programs with retailers and brands that align with our values and offer products we would confidently recommend.
              </p>
              
              <CalloutBox>
                <div className="flex items-start gap-3">
                  <Award className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Equal Evaluation</p>
                    <p className="text-sm">
                      Whether a product has an affiliate link or not, it is evaluated with the same standards of clarity, usefulness, and fairness.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Transparency and Trust */}
          <AccordionItem
            title="Transparency and Trust"
            isOpen={openSections.transparencyAndTrust}
            onToggle={() => toggleSection('transparencyAndTrust')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Affiliate relationships are always disclosed so you know when a link may generate a commission.
                We believe transparency builds trust — and trust is the foundation of Radiance.
              </p>
              <p className="text-sm text-gray-600">
                For more information about our editorial philosophy, see our <a href="/editorial-standards" className="text-[#ab3d22] hover:underline">Editorial Standards</a> page.
              </p>
            </div>
          </AccordionItem>

          {/* Contact */}
          <AccordionItem
            title="Contact Us"
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Have questions about our affiliate policies or editorial approach?
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#ab3d22]" />
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">Response Time:</span>
                    <span className="text-sm text-gray-600">Within 24–48 hours</span>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Your trust matters — and we are always here to answer your questions.
              </p>
            </div>
          </AccordionItem>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default AffiliateDisclosurePage;
